/** @module components/product-feed-layout */
import baustein from "baustein";
import analytics from "web/script/analytics/analytics";
import browser from "web/script/utils/browser";
import factory from "web/script/dom/factory";
import style from "web/script/dom/style";
import events from "web/script/dom/events";
import rothko from "web/script/modules/rothko";
import url from "web/script/utils/url";
import logging from "web/script/utils/logging";
import globals from "web/script/modules/globals";
import ProductFeed from "web/script/components/product-feed";
import store from "web/redux/store";
import { setFeedOrder } from "web/redux/ducks/feed-filters";


const MATTE_SELECTOR = ".product-feed-layout__matte";
const MATTE_SHOW_CLASS = "product-feed-layout__matte--show";

/**
 * The product feed layout controls the dynamic interactions between the Product Feed,
 * Filters and other components. We can't use a single template to define a layout for
 * product feeds, however, since they're different from page to page, so it's up to this
 * component to ensure some sanity.
 * @constructor
 * @alias module:components/product-feed-layout
 * @extends module:components/components
 */
export default baustein.register(
    "product-feed-layout",
    /** @lends module:components/product-feed-layout.prototype */
    {
        _currentRenderedPage: null,

        setupEvents(add) {
            const filters = this.findComponent("universal-filters");

            if (!filters) {
                console.info("No universal-filters found, using old filters");
                return;
            }

            add("openFilters", this._onOpenFilters);
            add("closeFilters", this.closeFilters);
            add("applyFilters", this.applyFilters);
            add("click", MATTE_SELECTOR, this.closeFilters);
            add("priceGroupOptionClicked", "quick-price-filter", this._onPriceGroupOptionClicked);
            add("feedSortingChanged", "sort-dropdown", this._onFeedSortingChanged);
            add("feedSortingChanged", "filter-button", this._onFeedSortingChanged);
        },

        init() {
            this.client = new rothko.Rothko("modules/product_feed");
            this.client.on("data", this._onGetFeedData.bind(this));

            // capture the initial page this loaded on
            let parsedUrl = url.parse(globals.window.location.href);
            this._currentRenderedPage = `${parsedUrl.path}?${parsedUrl.searchParams}`;
        },

        /**
         * When the event to open filters is fired, find the feed sidebar and show them, and then
         * display a matte behind it, if on a tablet.
         * @private
         */
        _onOpenFilters() {
            // call openFilters on universal filters
            const universalFiltersComponent = this.findComponent("universal-filters");
            this.invoke(universalFiltersComponent, "onOpenFilters");

            // only show a matte on tablet
            if (!browser.getViewport() === browser.VIEWPORTS.TABLET) {
                return;
            }

            // insert the matte in the dom, and add a class to fade it in
            const matteElem = factory.fromString('<div class="product-feed-layout__matte"></div>');
            this.el.appendChild(matteElem);
            style.forceReflow(this.el);
            matteElem.classList.add(MATTE_SHOW_CLASS);

            const feed = this.findComponent("product-feed");
            if (feed) {
                feed.disableInfiniteScroll();
            }
        },

        /**
         * When the event to close filters is fired, find the feed sidebar and close it, and remove
         * the matte if present.
         * @private
         */
        closeFilters() {
            const filters = this.findComponent("universal-filters");
            this.invoke(filters, "closeFilters");

            const matte = this.el.querySelector(MATTE_SELECTOR);
            if (matte) {
                events
                    .onTransitionEnd(matte, (e) => e.classList.remove(MATTE_SHOW_CLASS))
                    .then(() => this.el.removeChild(matte));
            }

            const feed = this.findComponent("product-feed");
            if (feed) {
                feed.enableInfiniteScroll();
            }
        },

        _onMoreLoaded() {
            const showMoreButton = this.findComponent("click-to-show-oos");
            if (showMoreButton) {
                showMoreButton.destroy();
            }
        },

        /**
         * Load the Quick Filters.
         */
        _loadFeedQuickFilters() {
            const existingQuickFiltersCard = this.findComponent("quick-filters-card");
            const universalFilters = this.findComponent("universal-filters");

            if (existingQuickFiltersCard) {
                existingQuickFiltersCard.loadWith(universalFilters.allFilters);
            }
        },

        /**
         * Update the query parameters when the filters change. Tracks it as a page view.
         *
         * @param event
         */
        applyFilters() {
            this.closeFilters();
        },

        /**
         * When the options change into the component, we should load a new feed.
         *
         * Once the feed has been loaded, replace the existing one with the new one. And then
         * load the filters.
         */
        onOptionsChange() {
            const feedUrl = this.options.url;
            if (!feedUrl || this._currentRenderedPage === feedUrl) {
                return;
            }

            this._loadNewFeed(feedUrl);
            this._loadNewFilters(feedUrl);
            this._currentRenderedPage = feedUrl;
        },

        _loadNewFeed(feedUrl) {
            const existingFeed = this.findComponent("product-feed");
            existingFeed.startLoading();

            this.client.fetch({ url: feedUrl }).catch((err) => {
                // if an error occurred, then reload the page, but first log
                logging.error(err);
                globals.window.location.reload();
            });
        },

        _onGetFeedData(ev, data) {
            const existingFeed = this.findComponent("product-feed");

            // create a new feed and filters components
            const feed = new ProductFeed({
                feedData: data,
                pagination: data.pagination,
                modifier: existingFeed.options.modifier,
                infiniteScrollEnabled: existingFeed.options.infiniteScrollEnabled,
                feedSortDropdown: data.feed_sort_dropdown,
            });

            // logging how many rows the user could see, normalised based on desktop
            const itemCount = data.feed_items.length;

            analytics.event("FEED", "POST_FILTER_ROWS", Math.min(Math.ceil(itemCount / 3), 4));

            // insert the new feed, delete the old one
            requestAnimationFrame(() => {
                feed.insertBefore(existingFeed.el);
                existingFeed.destroy();
            });
        },

        _loadNewFilters(feedUrl) {
            const urlData = url.parse(feedUrl);
            const filtersSourceUrl = urlData.searchParams.has("ref")
                ? urlData.searchParams.get("ref")
                : urlData.path;

            // find the existing ones (we already have the existing feed)
            const existingFilters = this.findComponent("universal-filters");

            // update the options, and the filters will handle the rest
            existingFilters.updateOptions({
                feedUrl: filtersSourceUrl,
                queryString: urlData.searchParams,
            });

            const existingQuickFilters = this.findComponent("quick-filters-card");
            // update the options, and the filters will handle the rest
            if (existingQuickFilters) {
                existingQuickFilters.updateOptions({
                    feedUrl: filtersSourceUrl,
                    queryString: urlData.searchParams,
                });
            }
        },

        _onPriceGroupOptionClicked(event) {
            const filters = this.findComponent("universal-filters");
            const quickPriceFilters = this.findComponent("quick-price-filter");

            if (!filters || !quickPriceFilters) {
                return;
            }

            const appliedFilters = filters.getSerializedForm();
            quickPriceFilters.applyClickedFilter(appliedFilters, event.href);
        },

        _onFeedSortingChanged(event) {
            const filters = this.findComponent("universal-filters");

            if (!filters) {
                return;
            }

            store.dispatch(setFeedOrder(event.sortOption));
        },
    }
);
